import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Zap, Crown, Shield, Sword, Castle, BookOpen, Trophy, Users, Bot, ArrowRight, CheckCircle, AlertTriangle, Sparkles, Target, Brain, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ChessAppLayout } from "@/components/ChessAppLayout";
import { useSEO } from "@/hooks/useSEO";
import { generateEducationalJsonLd, generateGameJsonLd } from "@/hooks/useSEO";

const HatchRules = () => {
  const navigate = useNavigate();

  useSEO({
    title: "Hatch Evolution Chess Rules - Revolutionary Chess Variant | ChessHatch",
    description: "Master Hatch Evolution Chess! Learn the revolutionary rules where captured pieces evolve, creating dynamic gameplay with endless strategic possibilities.",
    keywords: "hatch evolution chess, chess variant, piece evolution, chess rules, strategy game, innovative chess, dynamic chess, chess transformation",
    canonical: "/hatch-rules",
    ogImage: "/chess-hero.jpg",
    jsonLd: [
      generateEducationalJsonLd(
        "Hatch Evolution Chess Rules",
        "Complete guide to playing Hatch Evolution Chess, where pieces evolve when they capture opponents"
      ),
      generateGameJsonLd()
    ]
  });

  const evolutionChain = [
    { from: "Pawn", to: "Knight", icon: "♟️", color: "text-cyan-300", glowColor: "drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]", description: "Pawns become knights after first capture" },
    { from: "Knight", to: "Bishop", icon: "♞", color: "text-pink-400", glowColor: "drop-shadow-[0_0_10px_rgba(236,72,153,0.8)]", description: "Knights evolve into bishops" },
    { from: "Bishop", to: "Rook", icon: "♗", color: "text-yellow-300", glowColor: "drop-shadow-[0_0_10px_rgba(253,224,71,0.8)]", description: "Bishops transform into rooks" },
    { from: "Rook", to: "Queen", icon: "♜", color: "text-emerald-300", glowColor: "drop-shadow-[0_0_10px_rgba(110,231,183,0.8)]", description: "Rooks become the mighty queen" },
    { from: "Queen", to: "Queen", icon: "♛", color: "text-purple-400", glowColor: "drop-shadow-[0_0_10px_rgba(192,132,252,0.8)]", description: "Queens stay queens (maximum evolution)" },
    { from: "King", to: "King", icon: "♚", color: "text-gray-400", glowColor: "drop-shadow-[0_0_10px_rgba(156,163,175,0.8)]", description: "Kings never evolve (special piece)" }
  ];

  const excitementFactors = [
    {
      title: "Dynamic Strategy Revolution",
      description: "Every capture reshapes the battlefield. Traditional chess patterns become obsolete as new tactical possibilities emerge constantly.",
      icon: <TrendingUp className="w-6 h-6 text-primary" />,
      highlight: "🚀 Game-changing"
    },
    {
      title: "Epic Comeback Potential", 
      description: "Behind in material? Capture opponent pieces to transform your army and stage incredible comebacks that seemed impossible.",
      icon: <Trophy className="w-6 h-6 text-yellow-500" />,
      highlight: "⚡ Thrilling"
    },
    {
      title: "Advanced Tactical Complexity",
      description: "Plan multiple moves ahead considering not just position, but how transformations will reshape your entire strategic approach.",
      icon: <Brain className="w-6 h-6 text-purple-500" />,
      highlight: "🧠 Mind-bending"
    },
    {
      title: "Fresh Meta Every Game",
      description: "Traditional opening theory takes a backseat to adaptive thinking and evolutionary planning. Each game is unique.",
      icon: <Target className="w-6 h-6 text-green-500" />,
      highlight: "🎯 Innovative"
    }
  ];

  const strategicTips = [
    {
      title: "Early Game Evolution",
      tip: "Sacrifice pawns strategically to get knights early - they control different squares and can jump over pieces",
      icon: <Zap className="w-5 h-5 text-primary" />,
      level: "Beginner"
    },
    {
      title: "Defensive Positioning", 
      tip: "Keep valuable pieces protected - every opponent capture makes them stronger. Think defense first!",
      icon: <Shield className="w-5 h-5 text-blue-500" />,
      level: "Intermediate"
    },
    {
      title: "Evolution Chain Planning",
      tip: "Plan capture sequences to get pieces where their new abilities shine. Position for post-evolution power!",
      icon: <Crown className="w-5 h-5 text-purple-500" />,
      level: "Advanced"
    },
    {
      title: "Endgame Power Plays",
      tip: "Late-game captures can create overwhelming advantages. One capture chain can decide the entire game!",
      icon: <Trophy className="w-5 h-5 text-yellow-500" />,
      level: "Expert"
    }
  ];

  return (
    <ChessAppLayout>
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50 dark:from-violet-950 dark:via-fuchsia-900/30 dark:to-cyan-950 relative overflow-hidden">
        {/* Floating Particles - only in dark mode */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden hidden dark:block">
          <div className="absolute top-20 left-10 text-cyan-300 text-2xl animate-float-particle">✨</div>
          <div className="absolute top-40 right-20 text-pink-400 text-3xl animate-float-particle" style={{ animationDelay: '1s' }}>⭐</div>
          <div className="absolute bottom-40 left-1/4 text-yellow-300 text-2xl animate-float-particle" style={{ animationDelay: '2s' }}>💫</div>
          <div className="absolute bottom-20 right-1/3 text-emerald-300 text-3xl animate-float-particle" style={{ animationDelay: '3s' }}>✨</div>
          <div className="absolute top-1/2 right-10 text-purple-400 text-2xl animate-float-particle" style={{ animationDelay: '4s' }}>⭐</div>
        </div>
        {/* Header */}
        <div className="border-b border-border dark:border-pink-500/30 bg-card dark:bg-gradient-to-r dark:from-violet-900/50 dark:via-fuchsia-800/50 dark:to-cyan-900/50 backdrop-blur-sm">
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-foreground dark:text-cyan-200 hover:text-foreground/80 dark:hover:text-cyan-100"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Button>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => navigate('/?mode=hatch')}
                  size="sm"
                  className="flex items-center gap-2 bg-primary hover:bg-primary-hover text-primary-foreground dark:bg-gradient-to-r dark:from-pink-600 dark:via-purple-600 dark:to-cyan-600 dark:hover:from-pink-500 dark:hover:via-purple-500 dark:hover:to-cyan-500"
                >
                  <Zap className="w-4 h-4" />
                  Play Now
                </Button>
                <Badge className="bg-primary/10 text-primary border-primary/30 dark:bg-gradient-to-r dark:from-cyan-500/20 dark:to-pink-500/20 dark:text-cyan-200 dark:border-cyan-400/30">
                  Game Rules
                </Badge>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8 max-w-6xl">
          {/* Hero Section */}
          <div className="text-center mb-12 relative">
            <div className="inline-flex items-center gap-2 mb-4">
              <Sparkles className="w-8 h-8 text-primary dark:text-cyan-300 animate-pulse dark:drop-shadow-[0_0_12px_rgba(34,211,238,0.8)]" />
              <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary via-accent to-primary dark:from-cyan-200 dark:via-pink-300 dark:via-yellow-200 dark:to-purple-300 bg-[length:200%_auto] animate-rainbow-text bg-clip-text text-transparent">
                Hatch Evolution Chess
              </h1>
              <Sparkles className="w-8 h-8 text-accent dark:text-pink-400 animate-pulse dark:drop-shadow-[0_0_12px_rgba(236,72,153,0.8)]" />
            </div>
            <p className="text-xl text-foreground dark:text-cyan-50 max-w-3xl mx-auto leading-relaxed">
              Experience chess like never before! In Hatch Evolution, every capture transforms your pieces, 
              creating dynamic gameplay where strategy evolves with every move.
            </p>
          </div>

          {/* Core Rule Card */}
          <Card className="mb-8 border-pink-500/30 bg-gradient-to-br from-violet-900/30 via-fuchsia-800/30 to-cyan-900/30 animate-aurora-glow backdrop-blur-sm">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl flex items-center justify-center gap-2 text-cyan-100">
                <Crown className="w-6 h-6 text-yellow-300 drop-shadow-[0_0_8px_rgba(253,224,71,0.6)]" />
                The Core Transformation Rule
                <Crown className="w-6 h-6 text-yellow-300 drop-shadow-[0_0_8px_rgba(253,224,71,0.6)]" />
              </CardTitle>
              <CardDescription className="text-lg text-pink-200">
                The foundation of Hatch Evolution Chess
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center p-6 bg-muted/30 rounded-lg border">
                <p className="text-xl font-semibold text-foreground mb-2">
                  🎯 When a piece captures an opponent's piece, it immediately evolves!
                </p>
                <p className="text-muted-foreground mb-4">
                  This single rule transforms every capture from a simple material gain into a strategic evolution decision.
                </p>
                <div className="bg-background/50 p-4 rounded-lg border">
                  <div className="text-lg font-mono font-semibold text-primary">
                    Pawn → Knight → Bishop → Rook → Queen
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Why It's Exciting */}
          <Card className="mb-8 border-purple-500/20 bg-gradient-to-r from-purple-500/5 to-primary/5">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Trophy className="w-6 h-6 text-purple-500" />
                Why Hatch Evolution is Absolutely Thrilling
              </CardTitle>
              <CardDescription>
                Discover what makes this chess variant incredibly addictive
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {excitementFactors.map((factor, index) => (
                  <div key={index} className="relative p-6 rounded-lg border bg-gradient-to-br from-muted/30 to-background/50 hover:shadow-lg transition-all">
                    <Badge className="absolute top-2 right-2 text-xs bg-primary/10 text-primary border-primary/20">
                      {factor.highlight}
                    </Badge>
                    <div className="flex items-start gap-4">
                      <div className="p-2 rounded-lg bg-muted/50">
                        {factor.icon}
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-2">{factor.title}</h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">{factor.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Evolution Chain */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <ArrowRight className="w-6 h-6 text-primary" />
                Complete Evolution Chain
              </CardTitle>
              <CardDescription>
                How each piece transforms after capturing - your strategic roadmap
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {evolutionChain.map((evolution, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border transition-all hover:shadow-md hover:scale-105 ${
                      evolution.from === evolution.to 
                        ? 'bg-gradient-to-br from-gray-800/40 to-gray-900/40 border-gray-700/50' 
                        : 'bg-gradient-to-br from-violet-900/30 via-fuchsia-800/20 to-cyan-900/30 border-pink-500/30 hover:border-cyan-400/50'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`text-3xl ${evolution.glowColor}`}>{evolution.icon}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-cyan-100">{evolution.from}</span>
                        <ArrowRight className={`w-4 h-4 ${evolution.color}`} />
                        <span className={`font-semibold ${evolution.color}`}>{evolution.to}</span>
                      </div>
                    </div>
                    <p className="text-sm text-pink-200">{evolution.description}</p>
                    {evolution.from !== evolution.to && (
                      <Badge className="mt-2 text-xs bg-gradient-to-r from-cyan-500/20 to-pink-500/20 text-cyan-200 border-cyan-400/30">
                        Transforms on capture
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Strategic Mastery */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <BookOpen className="w-6 h-6 text-blue-500" />
                Strategic Mastery Guide
              </CardTitle>
              <CardDescription>
                Level up your Hatch Evolution gameplay with these essential strategies
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {strategicTips.map((tip, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 rounded-lg bg-muted/30 border hover:bg-muted/40 transition-colors">
                    <div className="p-2 rounded-lg bg-background/50">
                      {tip.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-foreground">{tip.title}</h4>
                        <Badge variant="outline" className="text-xs">
                          {tip.level}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">{tip.tip}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Special Rules */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-yellow-500" />
                Special Rules & Exceptions
              </CardTitle>
              <CardDescription>
                Master these important details to dominate the game
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-muted/30 border">
                    <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Crown className="w-4 h-4 text-purple-500" />
                      King Safety Rule
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Kings never evolve and remain the ultimate target. Protect your king while using evolution to create powerful attacking forces.
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/30 border">
                    <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Sword className="w-4 h-4 text-red-500" />
                      Pawn Promotion
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Pawns reaching the 8th rank promote normally to any piece you choose. If they capture during promotion, they evolve instead!
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-muted/30 border">
                    <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Castle className="w-4 h-4 text-blue-500" />
                      Castling Rules
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Standard castling rules apply unchanged. Evolution doesn't affect castling rights unless the king or rook actually moves.
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/30 border">
                    <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Zap className="w-4 h-4 text-green-500" />
                      En Passant Evolution
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      En passant captures trigger evolution! The capturing pawn immediately transforms into a knight - a powerful tactical weapon.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Call to Action */}
          <div className="text-center space-y-6">
            <Separator className="my-8" />
            
            <div>
              <h3 className="text-3xl font-bold text-foreground mb-4">
                Ready to Evolve Your Chess Game?
              </h3>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto text-lg">
                Experience the thrill of transformation, the excitement of evolution, and the strategy of adaptive gameplay. 
                Your chess journey will never be the same!
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                onClick={() => navigate('/?mode=hatch')}
                size="lg"
                className="bg-gradient-to-r from-pink-600 via-purple-600 to-cyan-600 hover:from-pink-500 hover:via-purple-500 hover:to-cyan-500 text-white px-8 py-3 text-lg animate-aurora-glow"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Play Hatch Evolution Now!
              </Button>
              
              <Button
                onClick={() => navigate('/?section=ai-squad')}
                variant="outline"
                size="lg"
                className="px-8 py-3 text-lg"
              >
                <Bot className="w-5 h-5 mr-2" />
                Challenge AI Opponents
              </Button>
              
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                size="lg"
                className="px-8 py-3 text-lg"
              >
                <Users className="w-5 h-5 mr-2" />
                Try Classic Chess
              </Button>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
              <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                <h4 className="font-semibold text-primary mb-2">🚀 New to Chess?</h4>
                <p className="text-sm text-muted-foreground">
                  Hatch Evolution is perfect for learning! The evolution mechanics teach piece values and movement patterns in an engaging, memorable way.
                </p>
              </div>
              <div className="p-4 bg-purple-500/5 rounded-lg border border-purple-500/20">
                <h4 className="font-semibold text-purple-500 mb-2">⚡ Chess Master?</h4>
                <p className="text-sm text-muted-foreground">
                  Challenge yourself with completely new strategic concepts! Traditional patterns won't save you here - adapt or lose!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ChessAppLayout>
  );
};

export default HatchRules;